import { useState, useCallback } from 'react';
import {
    Dialog,
    DialogContent,
    Box,
    Button,
    IconButton,
    Slider,
    Stack,
    Typography,
} from '@mui/material';
import Cropper from 'react-easy-crop';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CropIcon from '@mui/icons-material/Crop';
import CheckIcon from '@mui/icons-material/Check';

const ImageCropper = ({ open, onClose, imageSrc, onCropComplete }) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    const onCropCompleteCallback = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const createImage = (url) =>
        new Promise((resolve, reject) => {
            const image = new Image();
            image.addEventListener('load', () => resolve(image));
            image.addEventListener('error', error => reject(error));
            image.setAttribute('crossOrigin', 'anonymous');
            image.src = url;
        });

    const getCroppedImg = async (imageSrc, pixelCrop) => {
        const image = await createImage(imageSrc);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        const maxSize = Math.max(image.width, image.height);
        const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

        canvas.width = safeArea;
        canvas.height = safeArea;

        ctx.translate(safeArea / 2, safeArea / 2);
        ctx.translate(-safeArea / 2, -safeArea / 2);

        ctx.drawImage(
            image,
            safeArea / 2 - image.width * 0.5,
            safeArea / 2 - image.height * 0.5
        );

        const data = ctx.getImageData(0, 0, safeArea, safeArea);

        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;

        ctx.putImageData(
            data,
            Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
            Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
        );

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                resolve(URL.createObjectURL(blob));
            }, 'image/jpeg', 0.95);
        });
    };

    const handleSave = async () => {
        try {
            const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);

            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const image = await createImage(croppedImage);

            canvas.width = image.width;
            canvas.height = image.height;
            ctx.drawImage(image, 0, 0);

            const base64 = canvas.toDataURL('image/jpeg', 0.95).replace(
                /^data:image\/(?:gif|png|jpeg|bmp|webp)(?:;charset=utf-8)?;base64,/,
                ''
            );

            onCropComplete({
                base64,
                preview: croppedImage
            });

            onClose();
        } catch (error) {
            console.error('Erro ao recortar imagem:', error);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '20px',
                    overflow: 'hidden',
                    m: 2,
                    bgcolor: '#111',
                }
            }}
        >
            <DialogContent sx={{ p: 0 }}>
                {/* Header */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2.5,
                    py: 1.5,
                    bgcolor: '#111',
                }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <CropIcon sx={{ color: '#006a61', fontSize: 20 }} />
                        <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600 }}>
                            Ajustar foto
                        </Typography>
                    </Stack>
                    <IconButton onClick={onClose} size="small" sx={{ color: '#888', '&:hover': { color: 'white' } }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                {/* Crop area */}
                <Box sx={{
                    position: 'relative',
                    width: '100%',
                    height: { xs: 300, sm: 380 },
                    bgcolor: 'white',
                }}>
                    <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        aspect={1}
                        cropShape="round"
                        onCropChange={setCrop}
                        onCropComplete={onCropCompleteCallback}
                        onZoomChange={setZoom}
                        showGrid={false}
                        style={{
                            containerStyle: { background: 'white' },
                            cropAreaStyle: {
                                border: '3px solid #006a61',
                                boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)',
                            },
                        }}
                    />
                </Box>

                {/* Zoom controls */}
                <Box sx={{ px: 3, py: 2, bgcolor: '#111' }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <ZoomOutIcon sx={{ color: '#888', fontSize: 18 }} />
                        <Slider
                            value={zoom}
                            min={1}
                            max={3}
                            step={0.05}
                            onChange={(_, val) => setZoom(val)}
                            sx={{
                                color: '#006a61',
                                height: 4,
                                '& .MuiSlider-thumb': {
                                    width: 16,
                                    height: 16,
                                    bgcolor: 'white',
                                    border: '2px solid #006a61',
                                    '&:hover': { boxShadow: '0 0 0 6px rgba(0,106,97,0.2)' },
                                },
                                '& .MuiSlider-track': { border: 'none' },
                                '& .MuiSlider-rail': { bgcolor: '#333' },
                            }}
                        />
                        <ZoomInIcon sx={{ color: '#888', fontSize: 18 }} />
                    </Stack>
                </Box>

                {/* Actions */}
                <Box sx={{
                    display: 'flex',
                    gap: 1.5,
                    px: 3,
                    pb: 3,
                    pt: 0.5,
                    bgcolor: '#111',
                }}>
                    <Button
                        fullWidth
                        onClick={onClose}
                        variant="outlined"
                        sx={{
                            borderRadius: '10px',
                            height: 44,
                            textTransform: 'none',
                            fontWeight: 600,
                            borderColor: '#333',
                            color: '#aaa',
                            '&:hover': { borderColor: '#555', bgcolor: 'rgba(255,255,255,0.05)' },
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        fullWidth
                        onClick={handleSave}
                        variant="contained"
                        startIcon={<CheckIcon />}
                        sx={{
                            borderRadius: '10px',
                            height: 44,
                            textTransform: 'none',
                            fontWeight: 600,
                            bgcolor: '#006a61',
                            '&:hover': { bgcolor: '#005a52' },
                        }}
                    >
                        Aplicar
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default ImageCropper;
